const rawBasePath = (process.env.REACT_APP_BASE_PATH || '').trim();
const cleanBasePath = rawBasePath.replace(/^\/+|\/+$/g, '');
const normalizedBasePath = cleanBasePath ? `/${cleanBasePath}` : '';
const API_URL = process.env.REACT_APP_API_URL || `${normalizedBasePath}/api` || 'http://localhost:5000/api';

export const APP_CONFIG = {
  API_URL,
  TITLE: process.env.REACT_APP_TITLE || 'Uptime Monitor',
  REFRESH_INTERVAL: 15000,
};

export const STATUS_LABELS: Record<string, string> = {
  up: 'Operational',
  down: 'Down',
  pending: 'Pending',
  paused: 'Paused',
};

export const STATUS_COLORS: Record<string, string> = {
  up: '#16A34A',
  down: '#DC2626',
  pending: '#F59E0B',
  paused: '#6B7280',
};

export const ENVIRONMENT_TAGS = ['Dev', 'QA', 'Stage', 'Production'] as const;
export const BU_TAGS = ['CORP', 'SYSS', 'FCTL', 'MSOL'] as const;
export type EnvironmentTag = typeof ENVIRONMENT_TAGS[number];

export const MIN_INTERVAL_BY_ENV: Record<EnvironmentTag, number> = {
  Production: 60,
  Stage: 300,
  Dev: 600,
  QA: 600,
};

export const getMinIntervalForEnvironment = (env: string | null | undefined): number => {
  if (!env) return 60;
  return MIN_INTERVAL_BY_ENV[env as EnvironmentTag] ?? 60;
};

// Frequency options (seconds → label) for the monitor form.
export const INTERVAL_OPTIONS: { value: number; label: string }[] = [
  { value: 30, label: '30 seconds' },
  { value: 60, label: '1 minute' },
  { value: 120, label: '2 minutes' },
  { value: 300, label: '5 minutes' },
  { value: 600, label: '10 minutes' },
  { value: 900, label: '15 minutes' },
  { value: 1800, label: '30 minutes' },
  { value: 3600, label: '1 hour' },
];

export const HTTP_METHODS = ['GET', 'HEAD', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS'];

const parseApiDate = (dateStr: string): Date => {
  const raw = dateStr.trim();

  // SQLite/MSSQL often return UTC timestamps without a timezone suffix.
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(\.\d+)?$/.test(raw)) {
    return new Date(raw.replace(' ', 'T') + 'Z');
  }

  // ISO-like timestamp without timezone -> treat as UTC.
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(raw)) {
    return new Date(raw + 'Z');
  }

  return new Date(raw);
};

export const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '—';
  const date = parseApiDate(dateStr);
  const tz = window.localStorage.getItem('auth_timezone') || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const formatted = date.toLocaleString('en-US', {
    month: 'short', day: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: tz,
  });
  return `${formatted} ${tz}`;
};

export const formatRelative = (dateStr: string | null | undefined): string => {
  if (!dateStr) return 'never';
  const parsed = parseApiDate(dateStr);
  if (Number.isNaN(parsed.getTime())) return 'invalid time';
  const diff = Date.now() - parsed.getTime();
  const sec = Math.round(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
};

export const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds == null) return 'ongoing';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

export const formatUptime = (pct: number | null | undefined): string =>
  pct == null ? '—' : `${pct.toFixed(2)}%`;
